import { useMemo, ReactNode } from 'react';
import { CssBaseline } from '@mui/material';
import {
  createTheme,
  ThemeOptions,
  ThemeProvider,
  StyledEngineProvider,
  alpha
} from '@mui/material/styles';
import useSettings from '../hooks/useSettings';
import shape from './shape';
import palette from './palette';
import typography from './typography';
import breakpoints from './breakpoints';
import componentsOverride from './overrides';
import shadows, { customShadows } from './shadows';
import { useLanguage } from 'contexts/LanguageContext';

type ThemeConfigProps = {
  children: ReactNode;
};

export default function ThemeConfig({ children }: ThemeConfigProps) {
  const { themeMode, themeDirection, setColor } = useSettings();
  const { locale } = useLanguage();
  const isLight = themeMode === 'light';

  const theme = useMemo(() => {
    const themeOptions: ThemeOptions = {
      palette: isLight ? { ...palette.light, mode: 'light' } : { ...palette.dark, mode: 'dark' },
      shape,
      typography,
      breakpoints,
      direction: themeDirection,
      shadows: isLight ? shadows.light : shadows.dark,
      customShadows: isLight ? customShadows.light : customShadows.dark
    };

    if (themeOptions.palette) {
      themeOptions.palette.primary = setColor;
    }
    if (themeOptions.customShadows) {
      themeOptions.customShadows.primary = `0 8px 16px 0 ${alpha(setColor.main, 0.24)}`;
    }

    let baseTheme = createTheme(themeOptions);
    baseTheme.components = componentsOverride(baseTheme);
    baseTheme = createTheme(baseTheme, locale);

    return baseTheme;
  }, [isLight, themeDirection, setColor, locale]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
